import { InfiniteData } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { parseAnonymousLearningItem } from '../../models/AnonymousLearningItem'
import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import * as api from '../api'
import { useLearningItems } from '../entities'
import { learningItemActions } from '../store'
import { useApiInfiniteQuery } from '../useApiInfiniteQuery'
import { ReturnsLearningItems } from '../useInvalidate'

const learningItemFilterQueryParameters = queryParameters()
  .string('search')
  .commaSeparatedStringSet('categories')
  .boolean('favorites')
  .commaSeparatedStringSet('type')
  .commaSeparatedStringSet('status')
  .string('sort')

export default function useLearningItemFilter({ dontSearch }: { dontSearch?: boolean } = {}) {
  const dispatch = useDispatch()

  const { sort, categories, search, favorites, type, status, set, toUrlSearchParams } = useQueryParameters(
    learningItemFilterQueryParameters
  )

  const setFilter = useCallback(
    (values: { categories?: string[]; favorites?: boolean; type?: string[]; status?: string[] }) =>
      set(values, { preventScrollReset: true }),
    [set]
  )

  const setSearch = useCallback((search: string) => set({ search }, { preventScrollReset: true }), [set])

  const setSort = useCallback((sort: string) => set({ sort }, { preventScrollReset: true }), [set])

  const result = useApiInfiniteQuery({
    queryKey: [
      'learningItemFilter',
      ReturnsLearningItems,
      search,
      sort,
      categories,
      favorites,
      type,
      status,
      Boolean(dontSearch),
    ],

    placeholderData: (previousData?: InfiniteData<{ uuids: string[]; totalPages: number }, number>) =>
      previousData || {
        pages: [
          {
            uuids: ['dummy-1', 'dummy-2', 'dummy-3', 'dummy-4', 'dummy-5', 'dummy-6', 'dummy-7', 'dummy-8'],
            totalPages: 1,
          },
        ],
        pageParams: [0],
      },

    queryFn: async (a) => {
      if (dontSearch) {
        return { uuids: [], totalPages: 0 }
      }
      const res = await api.get(`course?${toUrlSearchParams()}&page_size=20&page=${a.pageParam + 1}`)
      const learningItems = parseAnonymousLearningItem.array(res.data)
      dispatch(learningItemActions.add(learningItems))
      return {
        uuids: learningItems.map(({ uuid }) => uuid),
        totalPages: res.lastPage as number,
      }
    },

    initialPageParam: 0,

    getNextPageParam: (lastPage, allPages, lastPageParam, _allPageParams) => {
      return lastPageParam + 1 < lastPage.totalPages ? lastPageParam + 1 : null
    },
  })

  const { learningItems } = useLearningItems(result.data!.pages.flatMap(({ uuids }) => uuids))

  return {
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetching: result.isFetching,
    categories,
    favorites,
    type,
    status,
    setFilter,
    search,
    setSearch,
    sort,
    setSort,
    toUrlSearchParams,
    learningItems,
    result,
  }
}
